import React from 'react';
import { injectIntl, IntlShape } from 'react-intl';
import { toast } from 'react-toastify';
import { cloneDeep, get, isEmpty, isNil } from 'lodash';
import { Grid } from '@material-ui/core';
import messages from 'containers/LawyerLegalProceedingsPage/judgementAdd/JudgementAddPronouncing/messages';
import { translate } from 'containers/services/translate';
import { IUploadedDocument } from 'containers/LawyerLegalProceedingsPage/typings';
import {
  EMPTY_INCOME_CERTIFICATE,
  INCOME_CERTIFICATE,
} from 'containers/data-structure';
import { BUTTON_COLORS } from 'constants/design';
import PrimaryButton from 'components/Buttons/PrimaryButton';
import { incomeCertificatesValidator } from 'containers/LawyerLegalProceedingsPage/judgementAdd/IncomeCertificate/form-validations';
import VictimCertificateForm from 'containers/LawyerLegalProceedingsPage/judgementAdd/IncomeCertificate/VictimCertificateForm';
import { formatCerificateToAdd } from 'containers/LawyerLegalProceedingsPage/judgementAdd/IncomeCertificate/utils';
import SecondaryButton from 'components/Buttons/SecondaryButton';
import { VictimType } from 'typings/globalTypes';
import { ClaimRecapType } from 'typings/claimTypes';
import { Listings } from 'containers/AppContainer/typings';

export interface IncomeCertificatesFormProps {
  intl: IntlShape;
  victims: VictimType[];
  uploadDocument: Function;
  uploadedDocument: IUploadedDocument;
  resetUploadedDocument: Function;
  sagisVictimInfo: any;
  requestCreateIncomeCertificate?: Function;
  isManager: Boolean;
  setDocumentId: Function;
  documentId: any;
  claimId: number;
  claimNumber: string;
  incomeCertificates: any;
  setIncomeCertificates: Function;
  judiciaryProcessId: any;
  dahirAmount: any;
  riskKey: string;
  claim: ClaimRecapType;
  setIsIncomeCertificateFormOn: Function;
  listings: Listings;
}

const IncomeCertificatesForm: React.FC<IncomeCertificatesFormProps> = (
  props,
): JSX.Element => {
  const validateIncomeCertificates = incomeCertificatesValidator(
    Object.values(props.incomeCertificates).filter((el: any) => !el?.id),
  );

  const handleChange = (victim: VictimType) => (
    event: React.ChangeEvent<{
      value: string;
      name: string;
      checked?: boolean;
    }>,
  ) => {
    const {
      target: { value, name, checked },
    } = event;

    switch (name) {
      case INCOME_CERTIFICATE.VICTIM_ID:
        if (checked) {
          props.setIncomeCertificates({
            ...props.incomeCertificates,
            [(victim?.id as unknown) as number]: {
              ...cloneDeep(EMPTY_INCOME_CERTIFICATE),
              victimId: victim.id,
              income: get(victim, 'annualOfIncome'),
            },
          });
        } else {
          const newObjectState = Object.fromEntries(
            Object.entries(props.incomeCertificates).filter(
              ([key]) => key !== String(victim.id),
            ),
          );
          props.setIncomeCertificates(newObjectState);
        }
        break;
      default:
        props.setIncomeCertificates({
          ...props.incomeCertificates,
          [(victim.id as unknown) as number]: {
            ...props.incomeCertificates[(victim.id as unknown) as number],
            [name]: value,
          },
        });
    }
  };

  const setDocument = (victimId: number) => (document: any) => {
    const incomeCertificatesData = Object.assign(
      {},
      { ...props.incomeCertificates },
    );
    if (String(victimId) === String(props.documentId)) {
      props.setIncomeCertificates({
        ...incomeCertificatesData,
        [victimId]: {
          ...document,
        },
      });
    }
    if (props.resetUploadedDocument) {
      props.resetUploadedDocument();
    }
  };
  const onValidate = () => {
    props.requestCreateIncomeCertificate &&
      props.requestCreateIncomeCertificate(
        formatCerificateToAdd(
          props.incomeCertificates,
          props.isManager,
          props.judiciaryProcessId,
          props.claimId,
          props.claimNumber,
          props.riskKey,
          props.victims,
          props.dahirAmount,
          props.claim,
        ),
        successCallBack,
      );
  };

  const onCancel = () => {
    const newObjectState = Object.values(props.incomeCertificates).filter(
      (el: any) => !isNil(get(el, 'id', null)),
    );
    props.setIncomeCertificates(newObjectState);
    props.setIsIncomeCertificateFormOn(false);
  };

  const successCallBack = () => {
    toast.success(translate(messages.incomeCertificateSuccessMessage, props));
  };

  return (
    <div className="sub_div">
      <span className="text">{translate(messages.selectVictims, props)}</span>
      <div className="check_box_container">
        {!isEmpty(props.victims) &&
          props.victims.map((victim: VictimType, index: number) => {
            return (
              <VictimCertificateForm
                victim={victim}
                index={index}
                uploadDocument={props.uploadDocument}
                uploadedDocument={props.uploadedDocument}
                resetUploadedDocument={props.resetUploadedDocument}
                sagisVictimInfo={props.sagisVictimInfo}
                requestCreateIncomeCertificate={
                  props.requestCreateIncomeCertificate
                }
                isManager={props.isManager}
                setDocumentId={props.setDocumentId}
                documentId={props.documentId}
                claimNumber={props.claimNumber}
                claimId={props.claimId}
                incomeCertificates={props.incomeCertificates}
                setIncomeCertificates={props.setIncomeCertificates}
                handleChange={handleChange}
                setDocument={setDocument}
                lossDate={get(props.claim, 'claim.lossDate') as Date}
                listings={props.listings}
              />
            );
          })}
      </div>
      <Grid
        container
        direction="row"
        justify="space-between"
        alignItems="center"
      >
        <Grid item>
          <SecondaryButton
            onClick={onCancel}
            title={translate(messages.cancelForm, props)}
          />
        </Grid>
        <Grid item>
          <PrimaryButton
            className="submit"
            onClick={onValidate}
            title={translate(messages.submitForm, props)}
            background={BUTTON_COLORS.PRIMARY_BLUE}
            border={BUTTON_COLORS.PRIMARY_BLUE}
            titlecolor={BUTTON_COLORS.BLUE_BUTTON_TITLE}
            disabled={
              !validateIncomeCertificates || isEmpty(props.incomeCertificates)
            }
          />
        </Grid>
      </Grid>
    </div>
  );
};

export default injectIntl(IncomeCertificatesForm);
